import { err, ok, type Result } from 'neverthrow';

import { loadTokens } from '@/lib/auth/tokens';

/**
 * Typed `fetch` wrapper for SPA → backend API calls.
 *
 * Reads the base URL from `VITE_API_URL` (injected at build time from
 * the staging Terraform output). Attaches `Authorization: Bearer {idToken}`
 * from the stored Cognito tokens when one exists — the backend's built-in
 * JWT authoriser validates it (ADR 0009).
 *
 * Returns a `Result<T, ApiError>` so callers can pattern-match per
 * `apps/web/CLAUDE.md` §5 rather than catching exceptions. The body
 * shape `T` is the typed JSON the endpoint returns.
 */

/**
 * Failure modes the SPA cares about.
 *
 * - `unauthorised` — backend returned 401. The caller usually wants to
 *   trigger a re-sign-in.
 * - `not_found` — 404. The caller usually surfaces a domain-specific
 *   message ("no record yet").
 * - `validation` — 400. Body explains what failed; surface verbatim.
 * - `server_error` — 5xx, network blip, or any unexpected status. Retry-
 *   able from the user's perspective.
 */
export type ApiError =
  | { readonly kind: 'unauthorised'; readonly message: string }
  | { readonly kind: 'not_found'; readonly message: string }
  | { readonly kind: 'validation'; readonly message: string }
  | { readonly kind: 'server_error'; readonly message: string };

interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly body?: unknown;
}

function apiBaseUrl(): string {
  const url: unknown = import.meta.env.VITE_API_URL;
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error(
      'Missing required env var: VITE_API_URL. ' +
        'Copy apps/web/.env.example to apps/web/.env.local and fill in the value from staging.',
    );
  }
  return url.replace(/\/$/, '');
}

async function parseErrorBody(response: Response): Promise<string> {
  // Try to pull a JSON `error` / `detail` field; fall back to the raw
  // text. The body might be plain-text (e.g. an API Gateway 502 page).
  const text = await response.text();
  if (!text) return response.statusText;
  try {
    const json = JSON.parse(text) as { error?: string; detail?: string };
    return json.detail ?? json.error ?? text;
  } catch {
    return text;
  }
}

/**
 * Internal — does the actual fetch, handles auth header, maps the
 * response into a Result. Callers (`api/me.ts`, future `api/orders.ts`)
 * wrap with a typed signature.
 */
export async function apiRequest<T>(options: RequestOptions): Promise<Result<T, ApiError>> {
  const tokens = loadTokens();
  const headers: Record<string, string> = {
    accept: 'application/json',
  };
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (tokens) {
    // Cognito's JWT authoriser validates the id_token (not the access
    // token) — the `aud` claim on the id_token is the SPA app client.
    headers.authorization = `Bearer ${tokens.idToken}`;
  }

  // Resolve the base URL outside the try block — a missing VITE_API_URL
  // is a build-time configuration bug, not a runtime network failure,
  // and should crash loudly rather than be swallowed as `server_error`.
  const url = `${apiBaseUrl()}${options.path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch (caught) {
    // Network failure, CORS preflight error, etc. — never reached the
    // server. Treat as server_error so the UI shows "try again".
    const message = caught instanceof Error ? caught.message : 'Network error.';
    return err({ kind: 'server_error', message });
  }

  if (response.ok) {
    // 204 No Content → no body; the caller's T should be `void` or
    // similar. JSON.parse('') throws so guard explicitly.
    const text = await response.text();
    if (text.length === 0) {
      return ok(undefined as T);
    }
    try {
      return ok(JSON.parse(text) as T);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Invalid JSON.';
      return err({ kind: 'server_error', message: `Could not parse response body: ${message}` });
    }
  }

  const message = await parseErrorBody(response);
  if (response.status === 401 || response.status === 403) {
    return err({ kind: 'unauthorised', message });
  }
  if (response.status === 404) {
    return err({ kind: 'not_found', message });
  }
  if (response.status === 400) {
    return err({ kind: 'validation', message });
  }
  return err({ kind: 'server_error', message: `${String(response.status)}: ${message}` });
}
